import React, { useState, useRef, useEffect, useCallback } from 'react';

interface RadiographyViewerProps {
  imageUrl: string;
  filename: string;
  onClose: () => void;
  metadata?: string;
}

type ToolMode = 'windowing' | 'pan' | 'zoom';

export default function RadiographyViewer({ imageUrl, filename, onClose, metadata }: RadiographyViewerProps) {
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [invert, setInvert] = useState(false);
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);

  const [activeTool, setActiveTool] = useState<ToolMode>('windowing');

  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const startPos = useRef({ x: 0, y: 0 });
  
  // Track start values before a drag begins
  const startValues = useRef({
    brightness: 100,
    contrast: 100,
    panX: 0,
    panY: 0,
    scale: 1,
  });

  const handleReset = () => {
    setBrightness(100);
    setContrast(100);
    setInvert(false);
    setScale(1);
    setPan({ x: 0, y: 0 });
    setRotation(0);
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    isDragging.current = true;
    startPos.current = { x: e.clientX, y: e.clientY };
    startValues.current = {
      brightness,
      contrast,
      panX: pan.x,
      panY: pan.y,
      scale,
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging.current) return;
    
    const deltaX = e.clientX - startPos.current.x;
    const deltaY = e.clientY - startPos.current.y;

    if (activeTool === 'windowing') {
      // Left/Right for Brightness, Up/Down for Contrast (DICOM standard)
      // Moving right increases brightness, moving up increases contrast
      const newBrightness = Math.max(0, startValues.current.brightness + deltaX * 0.5);
      const newContrast = Math.max(0, startValues.current.contrast - deltaY * 0.5);
      setBrightness(newBrightness);
      setContrast(newContrast);
    } else if (activeTool === 'pan') {
      setPan({
        x: startValues.current.panX + deltaX,
        y: startValues.current.panY + deltaY,
      });
    } else if (activeTool === 'zoom') {
      // Moving up zooms in, down zooms out
      const zoomFactor = Math.max(0.1, startValues.current.scale - deltaY * 0.01);
      setScale(zoomFactor);
    }
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    isDragging.current = false;
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
  };

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      setScale((prev) => Math.max(0.1, prev - e.deltaY * 0.005));
    };
    const node = containerRef.current;
    if (node) {
      node.addEventListener('wheel', handleWheel, { passive: false });
    }
    return () => {
      if (node) node.removeEventListener('wheel', handleWheel);
    };
  }, []);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: '#000',
      display: 'flex', flexDirection: 'column', zIndex: 9999,
      fontFamily: 'sans-serif'
    }}>
      {/* Header Toolbar */}
      <div style={{
        padding: '12px 20px', background: '#1e293b', borderBottom: '1px solid #334155',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#fff'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontWeight: 600, fontSize: '15px' }}>{filename}</span>
          {metadata && <span style={{ fontSize: '12px', color: '#94a3b8' }}>{metadata}</span>}
        </div>
        
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div style={{ display: 'flex', background: '#0f172a', padding: 4, borderRadius: 8, gap: 4 }}>
            <ToolButton active={activeTool === 'windowing'} onClick={() => setActiveTool('windowing')} title="Window Level (Brightness/Contrast)">🌗 B/C</ToolButton>
            <ToolButton active={activeTool === 'pan'} onClick={() => setActiveTool('pan')} title="Pan">✋ Pan</ToolButton>
            <ToolButton active={activeTool === 'zoom'} onClick={() => setActiveTool('zoom')} title="Zoom">🔍 Zoom</ToolButton>
          </div>
          
          <div style={{ width: '1px', height: '20px', background: '#334155', margin: '0 8px' }} />
          
          <ActionBtn onClick={() => setInvert(!invert)}>⬛ Invert</ActionBtn>
          <ActionBtn onClick={() => setRotation((prev) => prev - 90)}>⟲ Rotate L</ActionBtn>
          <ActionBtn onClick={() => setRotation((prev) => prev + 90)}>⟳ Rotate R</ActionBtn>
          
          <div style={{ width: '1px', height: '20px', background: '#334155', margin: '0 8px' }} />
          
          <ActionBtn onClick={handleReset}>↺ Reset</ActionBtn>
          <ActionBtn onClick={onClose} style={{ background: '#ef4444', borderColor: '#ef4444' }}>✕ Close</ActionBtn>
        </div>
      </div>
      
      {/* Main Viewport */}
      <div 
        ref={containerRef}
        style={{ flex: 1, position: 'relative', overflow: 'hidden', cursor: activeTool === 'pan' ? 'grab' : 'crosshair' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <img 
          src={imageUrl} 
          alt={filename}
          draggable={false}
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transformOrigin: 'center center',
            transform: `translate(calc(-50% + ${pan.x}px), calc(-50% + ${pan.y}px)) scale(${scale}) rotate(${rotation}deg)`,
            filter: `brightness(${brightness}%) contrast(${contrast}%) invert(${invert ? 100 : 0}%)`,
            maxHeight: '100%',
            maxWidth: '100%',
            pointerEvents: 'none',
          }}
        />

        {/* HUD Data */}
        <div style={{ position: 'absolute', bottom: 16, left: 16, color: '#10b981', fontSize: '13px', pointerEvents: 'none', textShadow: '1px 1px 2px #000' }}>
          B: {Math.round(brightness)}% C: {Math.round(contrast)}%<br/>
          Zoom: {Math.round(scale * 100)}%
        </div>
      </div>
    </div>
  );
}

function ToolButton({ children, active, onClick, title }: any) {
  return (
    <button
      title={title}
      onClick={onClick}
      style={{
        background: active ? '#3b82f6' : 'transparent',
        color: active ? '#fff' : '#94a3b8',
        border: 'none', padding: '6px 12px', borderRadius: 6,
        fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.1s'
      }}
    >
      {children}
    </button>
  );
}

function ActionBtn({ children, onClick, style }: any) {
  return (
    <button
      onClick={onClick}
      style={{
        background: 'transparent',
        color: '#e2e8f0',
        border: '1px solid #334155', padding: '6px 12px', borderRadius: 6,
        fontSize: '13px', fontWeight: 600, cursor: 'pointer', 
        transition: 'all 0.1s', ...style
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = '#334155')}
      onMouseLeave={(e) => {
        if (!style?.background || style.background === 'transparent') {
          e.currentTarget.style.background = 'transparent';
        }
      }}
    >
      {children}
    </button>
  );
}
